import { useEffect, useState } from 'react';
import {
  Button,
  Input,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogBody,
  DialogFooter,
} from '@vnetwork/ui/primitives';
import { useToast } from '@vnetwork/ui/feedback';
import { useRenameService } from '@/api/service-settings';
import { trackFeatureAction } from '@/lib/analytics-events';

const MAX_NAME_LEN = 255;

/**
 * Rename dialog for a Kubernetes service. Mirrors CdnRenameServiceDialog —
 * only the display name changes; clusters and zone assignments are untouched.
 */
export function KubernetesRenameServiceDialog({
  open,
  onOpenChange,
  serviceId,
  currentName,
}: {
  open: boolean;
  onOpenChange: (o: boolean) => void;
  serviceId: string;
  currentName: string;
}) {
  const { show } = useToast();
  const [name, setName] = useState(currentName);
  const rename = useRenameService(serviceId);

  useEffect(() => {
    if (open) setName(currentName);
  }, [open, currentName]);

  const trimmed = name.trim();
  const invalid = trimmed.length === 0 || trimmed.length > MAX_NAME_LEN;
  const unchanged = trimmed === currentName.trim();

  const submit = () => {
    if (invalid || unchanged || rename.isPending) return;
    rename.mutate(trimmed, {
      onSuccess: (res) => {
        trackFeatureAction('kubernetes', 'service', 'edit');
        show({ tone: 'success', title: `Renamed to ${res.service_name}` });
        onOpenChange(false);
      },
      onError: () => show({ tone: 'danger', title: 'Failed to rename service' }),
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Rename service</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <div style={{ display: 'grid', gap: 'var(--vn-space-3)' }}>
            <p style={{ margin: 0, color: 'var(--vn-fg-muted)', fontSize: 'var(--vn-font-size-sm)' }}>
              Choose a display name for this Kubernetes service. This changes
              the name only — clusters and zone assignments are unaffected.
            </p>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') submit(); }}
              placeholder="Service name"
              maxLength={MAX_NAME_LEN}
              autoFocus
              aria-label="Service name"
            />
          </div>
        </DialogBody>
        <DialogFooter>
          <Button variant="secondary" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button disabled={rename.isPending || invalid || unchanged} onClick={submit}>
            {rename.isPending ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
